/**
 * DoctorArticlesIndex — /dr/:slug/articles
 *
 * Index page that lists every published article authored under a single
 * doctor's slug. Powers the "See all N articles" overflow link from the
 * profile and gives Google a clean ItemList page to crawl.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '@/lib/api';
import { ArrowLeft, Newspaper, Stethoscope, Clock } from 'lucide-react';

export default function DoctorArticlesIndex() {
  const { slug } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [articles, setArticles] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get(`/public/doctor/${slug}/articles`),
      api.get(`/doctor/${slug}`),
    ])
      .then(([articlesRes, doctorRes]) => {
        if (cancelled) return;
        setArticles(articlesRes.data.articles || []);
        setDoctor(doctorRes.data);
      })
      .catch(() => { if (!cancelled) setError('Could not load articles'); });
    return () => { cancelled = true; };
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
        <div className="text-center">
          <p className="text-slate-500 mb-4">{error}</p>
          <Link to={`/dr/${slug}`} className="text-sky-600 hover:text-sky-700 text-sm font-medium">Back to profile</Link>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400 animate-pulse">Loading...</div>;
  }

  const canonicalUrl = `${window.location.origin}/dr/${slug}/articles`;
  const itemListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: articles.map((a, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      url: `${window.location.origin}/dr/${slug}/articles/${a.slug}`,
      name: a.title,
    })),
  };
  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: doctor.name, item: `${window.location.origin}/dr/${slug}` },
      { '@type': 'ListItem', position: 2, name: 'Articles', item: canonicalUrl },
    ],
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>{`Articles by ${doctor.name} — Health insights & guidance`}</title>
        <meta name="description" content={`Read patient-friendly health articles authored by ${doctor.name}. Reviewed for clinical accuracy and updated regularly.`} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={`Articles by ${doctor.name}`} />
        <meta property="og:description" content={`Health insights authored by ${doctor.name}.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        {doctor.photo_url && <meta property="og:image" content={doctor.photo_url} />}
        <script type="application/ld+json">{JSON.stringify(itemListJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      </Helmet>

      <div className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={`/dr/${slug}`} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-sky-600" data-testid="articles-back-to-doctor">
            <ArrowLeft size={14} />Back to {doctor.name}
          </Link>
          <span className="text-xs text-slate-500">{articles.length} article{articles.length === 1 ? '' : 's'}</span>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-6 py-10" data-testid="articles-index-body">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-bold text-sky-600 mb-3">
          <Newspaper size={12} />Health insights
        </div>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-slate-900 tracking-tight leading-tight mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Articles by {doctor.name}
        </h1>
        <p className="text-slate-600 text-base sm:text-lg max-w-2xl mb-10">
          Patient-friendly explanations of common conditions, procedures, and care decisions — authored by {doctor.name} and reviewed for clinical accuracy.
        </p>

        {articles.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-2xl p-10 text-center text-slate-500">
            No articles published yet. Check back soon.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {articles.map(a => {
              const readingMin = a.meta_description ? Math.max(3, Math.round(a.meta_description.split(/\s+/).length / 60)) : 3;
              return (
                <Link key={a.slug} to={`/dr/${slug}/articles/${a.slug}`}
                      className="block rounded-2xl border border-slate-200 bg-white p-5 hover:border-sky-300 hover:-translate-y-0.5 transition-all"
                      data-testid={`articles-index-card-${a.slug}`}>
                  <p className="text-[10px] uppercase tracking-[0.22em] font-bold text-sky-600 mb-1.5 flex items-center gap-1">
                    <Stethoscope size={11} />{a.primary_specialty || 'Health insight'}
                  </p>
                  <h2 className="font-semibold text-slate-900 leading-snug mb-2 text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {a.title}
                  </h2>
                  {a.meta_description && (
                    <p className="text-sm text-slate-600 leading-relaxed mb-3 line-clamp-3">{a.meta_description}</p>
                  )}
                  <p className="text-xs text-slate-500 flex items-center gap-1.5">
                    <Clock size={11} />{readingMin} min read
                    {a.published_at && (
                      <>
                        {' · '}
                        {new Date(a.published_at).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' })}
                      </>
                    )}
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 mt-16 py-8 text-center text-xs text-slate-400">
        Authored on <Link to={`/dr/${slug}`} className="text-sky-600 hover:underline">{doctor.name}'s DocSite</Link>
      </footer>
    </div>
  );
}
